import { Button, Modal, Table, Toggle } from '@itds/elements';
import React, { useContext, useEffect, useState } from 'react';

import { ACTION_MESSAGES } from '@carrier/utils/custom_hooks/useNotification';
import Api from '@carrier/utils/Api';
import { CarrierContext } from '@carrier/contexts/CarrierContext';
import { PropTypes } from 'prop-types';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const LifeQuotes = ({ carrier }) => {
  const navigate = useNavigate();

  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState(null);
  const [modal, setModal] = useState(false);
  const { setNotification } = useContext(CarrierContext);

  useEffect(() => {
    loadDataVersions();
  }, []);

  const loadDataVersions = () => {
    Api.loadDataVersions({ carrier_id: carrier.id }).then(({ data }) => {
      const sorted_versions = data.sort((a, b) => {
        return a.id - b.id;
      });

      setVersions(sorted_versions);
    });
  };

  const handleCreate = () => {
    Api.createDataVersion({ carrier_id: carrier.id }).then(() => {
      loadDataVersions();
    });
  };

  const toggleBeta = (version, e) => {
    e.stopPropagation();

    Api.updateDataVersion(version.id, {
      api_token: version.api_token,
      api_url: version.api_url,
      beta: !version.beta,
      carrier_id: carrier.id,
      date_gone_beta: new Date(),
      date_gone_live: version.date_gone_live,
      hide_premium_breakdown: version.hide_premium_breakdown,
      id: version.id,
      live: version.live,
      name: version.name,
      notes: version.notes
    }).then(() => {
      loadDataVersions();
    });
  };

  // NOTE: Hornet makes sure that only one data_version is live at a time
  const toggleLive = (version, e) => {
    e.stopPropagation();

    Api.updateDataVersion(version.id, {
      api_token: version.api_token,
      api_url: version.api_url,
      beta: version.beta,
      carrier_id: carrier.id,
      date_gone_beta: version.date_gone_beta,
      date_gone_live: new Date(),
      hide_premium_breakdown: version.hide_premium_breakdown,
      id: version.id,
      live: !version.live,
      name: version.name,
      notes: version.notes
    }).then(() => {
      loadDataVersions();
    });
  };

  const handleCopy = (version, e) => {
    e.stopPropagation();
    setModal('copy');
    setVersion(version);
  };

  const confirmCopy = () => {
    Api.copyDataVersion(version.id).then(() => {
      setModal(null);
      setVersion(null);
      setNotification(ACTION_MESSAGES.CARRIERS.LIFE_QUOTE.COPY);
      loadDataVersions();
    });
  };

  const handleDelete = (version, e) => {
    e.stopPropagation();
    setModal('delete');
    setVersion(version);
  };

  const confirmDelete = () => {
    window.scrollTo(0, 0);

    Api.deleteDataVersion(version.id)
      .then(() => {
        setModal(null);
        setVersion(null);
        loadDataVersions();
        setNotification(ACTION_MESSAGES.CARRIERS.LIFE_QUOTE.DELETE);
      })
      .catch(() => {
        setModal(null);
        setVersion(null);
        setNotification(ACTION_MESSAGES.CARRIERS.LIFE_QUOTE.DELETE_ERROR);
      });
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 30 }}>
        <Button onClick={handleCreate}>New Version</Button>
      </div>

      <Table
        columns={[
          {
            id: 'id',
            label: 'ID'
          },
          {
            id: 'name',
            label: 'Name'
          },
          {
            id: 'created_at',
            label: 'Created',
            render: version => format(new Date(version.created_at), 'MM/dd/yyyy')
          },
          {
            id: 'updated_at',
            label: 'Updated',
            render: version => format(new Date(version.updated_at), 'MM/dd/yyyy')
          },
          {
            id: 'beta',
            label: 'Beta',
            render: version => (
              <Toggle active={version.beta} onClick={toggleBeta.bind(null, version)} />
            )
          },
          {
            id: 'date_gone_beta',
            label: 'Date Gone Beta',
            render: version => {
              return version.date_gone_beta
                ? format(new Date(version.date_gone_beta), 'MM/dd/yyyy')
                : null;
            }
          },
          {
            id: 'live',
            label: 'Live',
            render: version => (
              <Toggle active={version.live} onClick={toggleLive.bind(null, version)} />
            )
          },
          {
            id: 'date_gone_live',
            label: 'Date Gone Live',
            render: quote => {
              return quote.date_gone_live
                ? format(new Date(quote.date_gone_live), 'MM/dd/yyyy')
                : null;
            }
          },
          {
            id: 'source',
            label: 'Source',
            render: version => (version.api_url ? 'External API' : 'IXN')
          },
          {
            id: 'actions',
            render: row => {
              return (
                <div>
                  <Button
                    inline={true}
                    onClick={handleCopy.bind(null, row)}
                    small={true}
                    style={{ marginRight: 5 }}
                  >
                    Copy
                  </Button>

                  <Button
                    color='RED'
                    inline={true}
                    onClick={handleDelete.bind(null, row)}
                    small={true}
                  >
                    Delete
                  </Button>
                </div>
              );
            },
            styles: { textAlign: 'right' }
          }
        ]}
        data={versions}
        onRowClick={r => {
          setVersion();
          navigate(`${r.id}`);
        }}
      />

      {modal === 'copy' ? (
        <Modal
          buttons={[
            {
              color: 'RED',
              children: 'Confirm Copy',
              onClick: confirmCopy,
              style: { marginLeft: 'auto' }
            }
          ]}
          contained={true}
          onClose={() => {
            setModal(null);
          }}
          title={`Copy "${version.id}"`}
        >
          <div style={{ padding: 40, fontSize: 16, textAlign: 'center', lineHeight: '1.5em' }}>
            Are you sure you want to copy version "{version.id}"?
          </div>
        </Modal>
      ) : null}

      {modal === 'delete' ? (
        <Modal
          buttons={[
            {
              color: 'RED',
              children: 'Confirm Delete',
              onClick: confirmDelete,
              style: { marginLeft: 'auto' }
            }
          ]}
          contained={true}
          onClose={() => {
            setModal(null);
          }}
          title={`Delete "${version.id}"`}
        >
          <div style={{ padding: 40, fontSize: 16, textAlign: 'center', lineHeight: '1.5em' }}>
            Are you sure you want to delete "{version.id}"?
          </div>
        </Modal>
      ) : null}
    </>
  );
};

LifeQuotes.propTypes = {
  carrier: PropTypes.object
};

export default LifeQuotes;
